/**
 * Mobile performance optimization utilities
 */

/**
 * Lazy load images on mobile
 */
export function lazyLoadImages() {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return;
  }

  const imageObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.classList.remove('lazy');
          observer.unobserve(img);
        }
      }
    });
  }, {
    rootMargin: '50px' // Start loading 50px before image enters viewport
  });

  document.querySelectorAll('img.lazy').forEach(img => {
    imageObserver.observe(img);
  });
}

/**
 * Reduce motion for users who prefer it
 */
export function respectReducedMotion() {
  if (typeof window === 'undefined') return;
  
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  if (prefersReducedMotion) {
    document.documentElement.style.setProperty('--animation-duration', '0.01ms');
    document.documentElement.classList.add('reduce-motion');
  }
}

/**
 * Optimize for slow connections
 */
export function optimizeForSlowConnection() {
  if (typeof window === 'undefined' || !('connection' in navigator)) {
    return;
  }

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  
  if (connection && (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g')) {
    // Disable animations
    document.documentElement.classList.add('slow-connection');
    
    // Load smaller images
    document.querySelectorAll('img[data-src-low]').forEach(img => {
      img.src = img.dataset.srcLow;
    });
  }
}

/**
 * Initialize all mobile optimizations
 */
export function initializeMobileOptimizations() {
  if (typeof window === 'undefined') return;
  
  lazyLoadImages();
  respectReducedMotion();
  optimizeForSlowConnection();
}

/**
 * Check if device is mobile
 */
export function isMobileDevice() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
}

/**
 * Check if device is touch-enabled
 */
export function isTouchDevice() {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

/**
 * Get safe area insets for notched devices
 */
export function getSafeAreaInsets() {
  if (typeof window === 'undefined') {
    return { top: 0, bottom: 0, left: 0, right: 0 };
  }

  const style = getComputedStyle(document.documentElement);
  return {
    top: parseInt(style.getPropertyValue('env(safe-area-inset-top)') || '0', 10),
    bottom: parseInt(style.getPropertyValue('env(safe-area-inset-bottom)') || '0', 10),
    left: parseInt(style.getPropertyValue('env(safe-area-inset-left)') || '0', 10),
    right: parseInt(style.getPropertyValue('env(safe-area-inset-right)') || '0', 10),
  };
}

